import { useState, useEffect, useCallback } from 'react';
import { HelpCircle, CheckCircle, Trash2, RefreshCw, Filter, Clock, Users, MessageSquare, ChevronDown, Save, X } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const OpenQuestionsPage = ({ user }) => {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total_open: 0, total_resolved: 0, by_role: {} });
  const [message, setMessage] = useState({ type: '', text: '' });
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('open');
  const [sortBy, setSortBy] = useState('count');
  const [filterDropdownOpen, setFilterDropdownOpen] = useState(false);
  
  // Edit state
  const [editingNote, setEditingNote] = useState(null);
  const [noteText, setNoteText] = useState('');

  const getToken = useCallback(() => {
    const savedUser = localStorage.getItem('gb-sylt-user');
    if (savedUser) {
      try {
        const parsed = JSON.parse(savedUser);
        return parsed.token;
      } catch (e) {
        return null;
      }
    }
    return null;
  }, []);

  const loadQuestions = useCallback(async () => {
    try {
      setLoading(true);
      const token = getToken();
      const response = await axios.get(`${API}/open-questions`, {
        params: { status: statusFilter, sort_by: sortBy },
        headers: { Authorization: `Bearer ${token}` }
      });
      setQuestions(response.data.questions || []);
      setStats(response.data.stats || { total_open: 0, total_resolved: 0, by_role: {} });
    } catch (error) {
      console.error('Load error:', error);
      setMessage({ type: 'error', text: 'Fehler beim Laden der offenen Fragen' });
    } finally {
      setLoading(false);
    }
  }, [getToken, statusFilter, sortBy]);

  useEffect(() => {
    loadQuestions();
  }, [loadQuestions]);

  useEffect(() => {
    if (message.text) {
      const timer = setTimeout(() => setMessage({ type: '', text: '' }), 5000);
      return () => clearTimeout(timer);
    }
  }, [message.text]);

  const handleMarkResolved = async (questionId) => {
    try {
      const token = getToken();
      await axios.put(`${API}/open-questions/${questionId}`, 
        { status: 'resolved' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessage({ type: 'success', text: 'Frage als erledigt markiert' });
      loadQuestions();
      // Update sidebar counter
      if (window.reloadOpenQuestionsCount) window.reloadOpenQuestionsCount();
    } catch (error) {
      setMessage({ type: 'error', text: 'Fehler beim Aktualisieren' });
    }
  };

  const handleReopen = async (questionId) => {
    try {
      const token = getToken();
      await axios.put(`${API}/open-questions/${questionId}`, 
        { status: 'open' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessage({ type: 'success', text: 'Frage wieder geöffnet' });
      loadQuestions();
      // Update sidebar counter
      if (window.reloadOpenQuestionsCount) window.reloadOpenQuestionsCount();
    } catch (error) {
      setMessage({ type: 'error', text: 'Fehler beim Aktualisieren' });
    }
  };

  const handleDelete = async (questionId) => {
    if (!window.confirm('Diese Frage wirklich löschen?')) return;
    try {
      const token = getToken();
      await axios.delete(`${API}/open-questions/${questionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessage({ type: 'success', text: 'Frage gelöscht' });
      loadQuestions();
      // Update sidebar counter
      if (window.reloadOpenQuestionsCount) window.reloadOpenQuestionsCount();
    } catch (error) {
      setMessage({ type: 'error', text: 'Fehler beim Löschen' });
    }
  };

  const handleSaveNote = async (questionId) => {
    try {
      const token = getToken();
      await axios.put(`${API}/open-questions/${questionId}`, 
        { admin_note: noteText },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessage({ type: 'success', text: 'Notiz gespeichert' });
      setEditingNote(null);
      loadQuestions();
    } catch (error) {
      setMessage({ type: 'error', text: 'Fehler beim Speichern der Notiz' });
    }
  };

  const formatDate = (isoString) => {
    if (!isoString) return '-';
    const date = new Date(isoString);
    return date.toLocaleDateString('de-DE', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getRoleBadge = (role) => {
    const config = {
      guest: { label: 'Gast', bg: 'bg-green-100', text: 'text-green-800' },
      employee: { label: 'Mitarbeiter', bg: 'bg-blue-100', text: 'text-blue-800' },
      admin: { label: 'Admin', bg: 'bg-purple-100', text: 'text-purple-800' }
    };
    const c = config[role] || config.guest;
    return (
      <span className={`px-2 py-0.5 rounded text-xs font-medium ${c.bg} ${c.text}`}>
        {c.label}
      </span>
    );
  };

  const getAreaLabel = (area) => {
    const labels = {
      guest: 'Gast-Wissen',
      employee: 'Mitarbeiter-Wissen',
      admin: 'Admin-Wissen'
    };
    return labels[area] || area;
  };

  return (
    <div className="h-full overflow-y-auto bg-gray-100">
      <div className="max-w-5xl mx-auto p-4 lg:p-6 space-y-4">
        
        {/* Header */}
        <div className="bg-white rounded-lg border p-4 flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-lg bg-amber-600 flex items-center justify-center">
              <HelpCircle className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Offene Fragen</h1>
              <p className="text-sm text-gray-500">Wissenslücken identifizieren und beheben</p>
            </div>
          </div>
          <button
            type="button"
            onClick={loadQuestions}
            disabled={loading}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center gap-2 text-sm font-medium"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Aktualisieren
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-white rounded-lg border p-4">
            <div className="text-2xl font-bold text-amber-600">{stats.total_open}</div>
            <div className="text-sm text-gray-500">Offen</div>
          </div>
          <div className="bg-white rounded-lg border p-4">
            <div className="text-2xl font-bold text-green-600">{stats.total_resolved}</div>
            <div className="text-sm text-gray-500">Erledigt</div>
          </div>
          <div className="bg-white rounded-lg border p-4">
            <div className="text-2xl font-bold text-gray-700">
              {(stats.by_role?.guest || 0) + (stats.by_role?.employee || 0) + (stats.by_role?.admin || 0)}
            </div>
            <div className="text-sm text-gray-500">Gesamt ({statusFilter})</div>
          </div>
          <div className="bg-white rounded-lg border p-4">
            <div className="flex gap-1 text-sm">
              <span className="text-green-600">{stats.by_role?.guest || 0} G</span>
              <span className="text-gray-300">|</span>
              <span className="text-blue-600">{stats.by_role?.employee || 0} M</span>
              <span className="text-gray-300">|</span>
              <span className="text-purple-600">{stats.by_role?.admin || 0} A</span>
            </div>
            <div className="text-sm text-gray-500">Nach Rolle</div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg border p-4 flex flex-wrap gap-3">
          {/* Status Filter */}
          <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
            <button
              type="button"
              onClick={() => setStatusFilter('open')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                statusFilter === 'open' ? 'bg-white text-amber-600 shadow-sm' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Offen
            </button>
            <button
              type="button"
              onClick={() => setStatusFilter('resolved')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                statusFilter === 'resolved' ? 'bg-white text-green-600 shadow-sm' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Erledigt
            </button>
            <button
              type="button"
              onClick={() => setStatusFilter('all')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                statusFilter === 'all' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Alle
            </button>
          </div>

          {/* Sort Dropdown */}
          <div className="relative">
            <button
              type="button"
              onClick={() => setFilterDropdownOpen(!filterDropdownOpen)}
              className="px-3 py-1.5 border border-gray-300 rounded-lg flex items-center gap-2 text-sm hover:bg-gray-50"
            >
              <Filter className="h-4 w-4 text-gray-500" />
              Sortierung: {sortBy === 'count' ? 'Häufigkeit' : sortBy === 'date' ? 'Datum' : sortBy === 'role' ? 'Rolle' : 'Bereich'}
              <ChevronDown className="h-4 w-4 text-gray-400" />
            </button>
            <div 
              className="absolute z-10 mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg"
              style={{ display: filterDropdownOpen ? 'block' : 'none' }}
            >
              {[
                { value: 'count', label: 'Häufigkeit (meiste zuerst)' },
                { value: 'date', label: 'Datum (neueste zuerst)' },
                { value: 'role', label: 'Nach Rolle' },
                { value: 'area', label: 'Nach Wissensbereich' }
              ].map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => { setSortBy(opt.value); setFilterDropdownOpen(false); }}
                  className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 first:rounded-t-lg last:rounded-b-lg ${
                    sortBy === opt.value ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Message */}
        <div style={{ minHeight: '48px' }}>
          <div 
            className={`p-3 rounded-lg border flex items-center justify-between ${
              message.type === 'error' ? 'bg-red-100 text-red-800 border-red-200' :
              message.type === 'success' ? 'bg-green-100 text-green-800 border-green-200' :
              'bg-blue-100 text-blue-800 border-blue-200'
            }`}
            style={{ display: message.text ? 'flex' : 'none' }}
          >
            <span className="text-sm">{message.text}</span>
            <button type="button" onClick={() => setMessage({ type: '', text: '' })} className="p-1 hover:opacity-70">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Loading */}
        <div style={{ display: loading ? 'block' : 'none' }} className="bg-white rounded-lg border p-8 text-center">
          <RefreshCw className="h-8 w-8 animate-spin mx-auto text-gray-400 mb-2" />
          <p className="text-gray-500">Lade offene Fragen...</p>
        </div>

        {/* Empty State */}
        <div 
          className="bg-white rounded-lg border p-8 text-center"
          style={{ display: !loading && questions.length === 0 ? 'block' : 'none' }}
        >
          <CheckCircle className="h-12 w-12 mx-auto text-green-400 mb-3" />
          <h3 className="text-lg font-medium text-gray-900 mb-1">
            {statusFilter === 'open' ? 'Keine offenen Fragen' : 'Keine Fragen gefunden'}
          </h3>
          <p className="text-gray-500 text-sm">
            {statusFilter === 'open' 
              ? 'Alle Wissenslücken wurden bearbeitet!' 
              : 'Ändern Sie die Filter um andere Fragen zu sehen.'}
          </p>
        </div>

        {/* Questions List */}
        <div 
          className="space-y-3"
          style={{ display: !loading && questions.length > 0 ? 'block' : 'none' }}
        >
          {questions.map((q, idx) => (
            <div 
              key={`question-${q.id}-${idx}`}
              className={`bg-white rounded-lg border overflow-hidden ${q.status === 'resolved' ? 'opacity-75' : ''}`}
            >
              <div className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    {/* Question */}
                    <div className="flex items-start gap-2 mb-2">
                      <MessageSquare className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                      <p className="text-gray-900 font-medium">{q.question}</p>
                    </div>
                    
                    {/* Meta info */}
                    <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
                      {getRoleBadge(q.role)}
                      <span className="px-2 py-0.5 rounded bg-gray-100 text-gray-600">
                        {getAreaLabel(q.knowledge_area)}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {formatDate(q.last_asked)}
                      </span>
                      {q.count > 1 && (
                        <span className="px-2 py-0.5 rounded bg-red-100 text-red-700 font-medium">
                          {q.count}× gefragt
                        </span>
                      )}
                      {q.status === 'resolved' && (
                        <span className="px-2 py-0.5 rounded bg-green-100 text-green-700">
                          ✓ Erledigt
                        </span>
                      )}
                    </div>

                    {/* Admin Note */}
                    {editingNote === q.id ? (
                      <div className="mt-3 flex gap-2">
                        <input
                          type="text"
                          value={noteText}
                          onChange={(e) => setNoteText(e.target.value)}
                          className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                          placeholder="Notiz eingeben..."
                        />
                        <button
                          type="button"
                          onClick={() => handleSaveNote(q.id)}
                          className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700"
                        >
                          <Save className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setEditingNote(null)}
                          className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm hover:bg-gray-50"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ) : q.admin_note ? (
                      <div 
                        className="mt-2 px-3 py-2 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800 cursor-pointer hover:bg-yellow-100"
                        onClick={() => { setEditingNote(q.id); setNoteText(q.admin_note || ''); }}
                      >
                        <strong>Notiz:</strong> {q.admin_note}
                      </div>
                    ) : null}
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col gap-1">
                    {q.status === 'open' ? (
                      <button
                        type="button"
                        onClick={() => handleMarkResolved(q.id)}
                        className="p-2 text-green-600 hover:bg-green-50 rounded-lg"
                        title="Als erledigt markieren"
                      >
                        <CheckCircle className="h-5 w-5" />
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => handleReopen(q.id)}
                        className="p-2 text-amber-600 hover:bg-amber-50 rounded-lg"
                        title="Wieder öffnen"
                      >
                        <HelpCircle className="h-5 w-5" />
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => { setEditingNote(q.id); setNoteText(q.admin_note || ''); }}
                      className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg"
                      title="Notiz hinzufügen"
                    >
                      <MessageSquare className="h-5 w-5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(q.id)}
                      className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                      title="Löschen"
                    >
                      <Trash2 className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Info */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-800">
          <strong>Hinweis:</strong> Hier erscheinen automatisch alle Fragen, die Samira nicht beantworten konnte.
          Nutzen Sie diese Liste, um das Wissen in der Wissensverwaltung gezielt zu erweitern.
        </div>

      </div>
    </div>
  );
};
